import classNames from 'classnames/bind';
import { useField } from 'formik';
import React from 'react';

import { InputGroup } from './InputGroup';
import { ScrollToError } from '@containers/Form/ScrollToError/ScrollToError';

import styles from './Input.module.scss';
import EyeIcon from '@icons/eye.svg';

export interface IInput {
  id: string;
  label?: string;
  required?: boolean;
  optional?: boolean;
  className?: string;
  testId?: string;
  validate?: (value: string) => void;
}

interface IProps extends IInput {
  name: string;
  type: React.InputHTMLAttributes<HTMLInputElement>['type'];
  placeholder?: React.InputHTMLAttributes<HTMLInputElement>['placeholder'];
  containerClassName?: string;
  tooltipText?: string;
  hideErrorOnBlur?: boolean;
  autofocus?: boolean;
  disableErrorScroll?: boolean;
  showValidationIcon?: boolean;
  autocomplete?: string;
}

const cx = classNames.bind(styles);

export const Input: React.FC<IProps> = ({
  id,
  name,
  type,
  validate,
  placeholder,
  label,
  required,
  optional,
  className,
  containerClassName,
  tooltipText,
  hideErrorOnBlur,
  testId,
  autofocus,
  disableErrorScroll,
  showValidationIcon = true,
  autocomplete,
}) => {
  const [field, meta] = useField({ name, validate });
  const [inputType, setInputType] = React.useState(type);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autofocus]);

  return (
    <>
      {!disableErrorScroll && <ScrollToError inputRef={inputRef} name={name} />}
      <InputGroup
        className={cx(type === 'hidden' ? styles.hidden : containerClassName, className)}
        error={meta.error}
        id={id}
        label={label}
        optional={optional}
        required={required}
        showError={hideErrorOnBlur ? meta.touched : true}
        showValidationIcon={showValidationIcon}
        testId={testId ?? name}
        tooltipText={tooltipText}
        touched={meta.touched}
        value={meta.value}
        isInput
      >
        <input
          {...field}
          ref={inputRef}
          autoComplete={autocomplete}
          className={cx('input', {
            inputError: meta.touched && meta.error,
            hidden: type === 'hidden',
          })}
          data-hj-suppress={inputType === 'password' ? true : undefined}
          data-testid={testId ?? name}
          id={id}
          placeholder={placeholder}
          type={inputType}
          onBlur={field.onBlur}
        />
        {type === 'password' && (
          <div className={styles.showPasswordWrapper}>
            <button
              className={styles.showPassword}
              data-testid={inputType === 'password' ? 'Show' : 'Hide'}
              tabIndex={-1}
              type="button"
              onClick={() => setInputType(inputType === 'password' ? 'text' : 'password')}
            >
              {inputType === 'password' ? 'Show' : 'Hide'}
              <span className={cx('iconWrapper', { iconCrossed: inputType !== 'password' })}>
                <EyeIcon className={styles.eyeIcon} />
              </span>
            </button>
          </div>
        )}
      </InputGroup>
    </>
  );
};
